import React, { useState, useEffect } from "react";
import { Bell, Cloud, DollarSign, X } from "lucide-react";
import { useSelector } from "react-redux";
import { jwtDecode } from "jwt-decode";
import { BarChart3, Network, Calendar, ShieldEllipsis } from "lucide-react";
import axios from "axios";

const WelcomePage = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [releaseNotes, setReleaseNotes] = useState([]);
  
  const idToken = useSelector((state) => state.idToken.value);
  const accessToken = useSelector((state) => state.accessToken.value);
  const apiUrl = process.env.REACT_APP_API_URL;
  let userName = "User";
  try{
    const decoded = jwtDecode(idToken);
    userName = `${decoded.given_name} ${decoded.family_name}`;
  }catch(err){
    console.log("Error decoding JWT: ", err);
  }

  useEffect(() => {
    const fetchReleaseNotes = async () => {
      const query = `
        query GetReleaseConfigurations {
          getReleaseConfigurations {
            id
            version
            date
            ReleaseType
            features {
              title
              description
            }
          }
        }
      `;

      try {
        const response = await axios.post(
          apiUrl,
          { query },
          {
            headers: {
              Authorization: accessToken,
            },
          }
        );

        if (response.data.errors) {
          throw new Error(response.data.errors[0].message);
        }

        const sortedReleaseNotes = response.data.data.getReleaseConfigurations
          .sort((a, b) => new Date(b.date) - new Date(a.date));
        
        setReleaseNotes(sortedReleaseNotes);
      } catch (err) {
        console.log(err)
      }
    };

    fetchReleaseNotes();
  }, [accessToken, apiUrl]);


  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      <div className="max-w-6xl mx-auto px-4 py-12">
        {/* Welcome Section */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-[#003366] mb-4">
            Welcome to CloudLens, {userName}
          </h1>
          <p className="text-lg text-gray-600">
            Your complete cloud monitoring and management solution
          </p>
        </div>

        {/* Feature Icons Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
          <FeatureCard
            icon={
              <Network className="w-8 h-8 flex-shrink-0 text-[#003366] group-hover:text-[#003366]" />
            }
            title="Endpoint Monitoring"
            description="Track availability and performance metrics in real-time"
            href="/endpoint"
          />
          <FeatureCard
            icon={
              <BarChart3 className="w-8 h-8 flex-shrink-0 text-[#003366] group-hover:text-[#003366]" />
            }
            title="Performance Metrics"
            description="Comprehensive performance analytics and insights"
            href="/performance"
          />
          <FeatureCard
            icon={
              <Cloud className="w-8 h-8 flex-shrink-0 text-[#003366] group-hover:text-[#003366]" />
            }
            title="Log Monitor"
            href="/logMonitor"
            description="Centralized logging and monitoring system"
          />
          <FeatureCard
            icon={
              <ShieldEllipsis className="w-8 h-8 flex-shrink-0 text-[#003366] group-hover:text-[#003366]" />
            }
            href="/security"
            title="Security"
            description="Detailed activity logging and compliance tracking"
          />
          <FeatureCard
            icon={
              <Calendar className="w-8 h-8 flex-shrink-0 text-[#003366] group-hover:text-[#003366]" />
            }
            href="/resourceScheduler"
            title="Resource Scheduler"
            description="Optimize resource allocation and scheduling"
          />
          <FeatureCard
            icon={
              <DollarSign className="w-8 h-8 flex-shrink-0 text-[#003366] group-hover:text-[#003366]" />
            }
            href="/cost"
            title="Cost Management"
            description="Track and optimize your cloud spending"
          />
        </div>
        {/* What's New Section */}
        <div
          onClick={() => setIsModalOpen(true)}
          className="bg-[#003366] rounded-lg overflow-hidden shadow-xl cursor-pointer transform transition-all duration-300 hover:shadow-2xl hover:-translate-y-1"
        >
          <div className="p-6 md:p-8 flex items-center justify-between">
            <div className="flex items-center space-x-6">
              <div className="bg-white/10 rounded-full p-3">
                <Bell className="w-6 h-6 text-white" />
              </div>
              <div className="text-white">
                <h2 className="text-2xl font-bold mb-2">
                  What's New in CloudLens
                </h2>
                <p className="text-blue-100">
                  Discover our latest features and improvements
                </p>
              </div>
            </div>
            <div className="hidden md:flex items-center">
              <span className="bg-white text-[#003366] px-4 py-2 rounded-lg font-medium hover:bg-blue-50 transition-colors">
                View Updates
              </span>
            </div>
          </div>
        </div>

        {/* Release Notes Modal */}
        {isModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl max-w-4xl w-full max-h-[80vh] overflow-hidden">
              <div className="p-6 border-b flex justify-between items-center bg-[#003366]/5">
                <h2 className="text-2xl font-bold text-[#003366]">
                  Latest Updates
                </h2>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="overflow-y-auto p-6 max-h-[calc(80vh-120px)]">
                {releaseNotes.map((release, idx) => (
                  <div
                    key={release.id}
                    className={`${idx !== 0 ? "mt-8" : ""} pb-8 ${idx !== releaseNotes.length - 1 ? "border-b" : ""}`}
                  >
                    <div className="flex items-center gap-4 mb-4">
                      <div>
                        <h3 className="text-xl font-bold text-[#003366]">
                          {release.version}
                        </h3>
                        <div className="flex items-center gap-3 mt-1">
                          <span className="text-sm text-gray-600">
                            {release.date}
                          </span>
                          <span className="px-2 py-1 bg-[#003366]/10 text-[#003366] text-xs rounded-full">
                            {release.type}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-4">
                      {release.features.map((feature, featureIdx) => (
                        <div
                          key={featureIdx}
                          className="pl-4 border-l-2 border-[#003366]/20"
                        >
                          <h4 className="font-semibold text-gray-900">
                            {feature.title}
                          </h4>
                          <p className="text-gray-600 mt-1">
                            {feature.description}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const FeatureCard = ({ icon, title, description, href }) => {
  return (
    <div 
      className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300"
    >
      <div className="flex flex-col items-center text-center">
        {icon}
        <h3 className="text-xl font-semibold text-gray-800 mt-4 mb-2">
          {title}
        </h3>
        <p className="text-gray-600">{description}</p>
      </div>
    </div>
  );
};

export default WelcomePage;